/**
 * Cache management utilities for handling browser cache and service workers
 */

/**
 * Check if the app has been updated and prompt user to refresh
 */
export const checkForAppUpdate = () => {
  // Check if service worker is supported
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // New service worker has taken control, app has been updated
      console.log('App update detected - new service worker active');
      
      // Show update notification to user
      if (!window.appUpdateNotified) {
        window.appUpdateNotified = true;
        showUpdateNotification();
      }
    });
  }
  
  // Don't check version from meta tag as it's not being replaced properly
  // checkVersionUpdate();
};

/**
 * Check version from meta tag
 */
const checkVersionUpdate = () => {
  const metaVersion = document.querySelector('meta[name="app-version"]');
  if (metaVersion) {
    const currentVersion = metaVersion.content;
    const storedVersion = localStorage.getItem('app-version');
    const notificationShown = sessionStorage.getItem('version-notification-shown');
    
    if (storedVersion && storedVersion !== currentVersion && notificationShown !== currentVersion) {
      console.log(`App version changed from ${storedVersion} to ${currentVersion}`);
      // Mark that we've shown notification for this version
      sessionStorage.setItem('version-notification-shown', currentVersion);
      showUpdateNotification();
    } else if (!storedVersion) {
      localStorage.setItem('app-version', currentVersion);
    }
  }
};

/**
 * Clear all application caches
 */
export const clearAllCaches = async () => {
  console.log('Clearing all application caches...');
  
  // Preserve auth-related data from localStorage
  const preserveKeys = [];
  const keysToPreserve = ['token', 'app-version'];
  
  // Also preserve MSAL auth data (keys that start with 'msal.')
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (keysToPreserve.includes(key) || key.startsWith('msal.'))) {
      preserveKeys.push({ key, value: localStorage.getItem(key) });
    }
  }
  
  // Clear localStorage
  localStorage.clear();
  
  // Restore preserved data
  preserveKeys.forEach(({ key, value }) => {
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  });
  
  // DO NOT clear sessionStorage as it may contain active auth sessions
  // sessionStorage.clear();
  
  // Clear service worker caches
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('Service worker caches cleared');
    } catch (error) {
      console.error('Error clearing service worker caches:', error);
    }
  }
  
  // Clear any in-memory caches in storage services
  if (window.storageService && window.storageService.clearCache) {
    window.storageService.clearCache();
  }
};

/**
 * Show update notification to user
 */
export const showUpdateNotification = () => {
  // Create notification element
  const notification = document.createElement('div');
  notification.id = 'app-update-notification';
  notification.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background-color: #1e40af;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 9999;
      display: flex;
      align-items: center;
      gap: 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    ">
      <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      <span>A new version is available!</span>
      <button onclick="(() => { 
        const metaVersion = document.querySelector('meta[name=\\'app-version\\']');
        if (metaVersion) {
          localStorage.setItem('app-version', metaVersion.content);
          sessionStorage.setItem('version-notification-shown', metaVersion.content);
        }
        window.location.reload(true);
      })()" style="
        background-color: white;
        color: #1e40af;
        padding: 6px 16px;
        border-radius: 4px;
        border: none;
        font-weight: 500;
        cursor: pointer;
        margin-left: 12px;
      ">
        Refresh Now
      </button>
      <button onclick="document.getElementById('app-update-notification').remove()" style="
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        padding: 4px;
        margin-left: 8px;
      ">
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  `;
  
  // Remove any existing notification
  const existing = document.getElementById('app-update-notification');
  if (existing) existing.remove();
  
  // Add to body
  document.body.appendChild(notification);
  
  // Auto-hide after 30 seconds
  setTimeout(() => {
    const element = document.getElementById('app-update-notification');
    if (element) element.remove();
  }, 30000);
};

/**
 * Force refresh with cache bypass
 */
export const forceRefresh = () => {
  // Clear all caches first
  clearAllCaches().then(() => {
    // Force reload bypassing cache
    window.location.reload(true);
  });
};